import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { getBackofficeRole } from "@/lib/admin-api";

export const Route = createFileRoute("/login")({
  head: () => ({
    meta: [
      { title: "Login, OM Selected" },
      { name: "description", content: "Anmeldung für die OM Selected Redaktion." },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: LoginPage,
});

function LoginPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, loading } = useAuth();
  const [mode, setMode] = useState<"signin" | "signup">("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  useEffect(() => {
    if (!loading && user) {
      getBackofficeRole(user.id).then((role) => {
        navigate({ to: role ? "/admin" : "/account/profile" });
      });
    }
  }, [loading, user, navigate]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      if (mode === "signin") {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
        const { data: { user: u } } = await supabase.auth.getUser();
        const role = u ? await getBackofficeRole(u.id) : null;
        navigate({ to: role ? "/admin" : "/account/profile" });
      } else {
        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: { emailRedirectTo: `${window.location.origin}/admin` },
        });
        if (error) throw error;
        setInfo(t("auth.confirmEmailInfo"));
      }
    } catch (err: any) {
      setError(err.message ?? t("auth.signinError"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto max-w-md px-5 md:px-10 py-20">
      <div className="eyebrow text-muted-foreground">{t("auth.editorial")}</div>
      <h1 className="serif text-4xl md:text-5xl mt-4">{mode === "signin" ? t("auth.signinTitle") : t("auth.signupTitle")}</h1>
      <form onSubmit={onSubmit} className="mt-10 space-y-6">
        <label className="block">
          <div className="eyebrow text-muted-foreground mb-2">{t("common.email")}</div>
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            className="w-full border-b border-foreground bg-transparent py-2 outline-none" />
        </label>
        <label className="block">
          <div className="eyebrow text-muted-foreground mb-2">{t("common.password")}</div>
          <input type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)}
            className="w-full border-b border-foreground bg-transparent py-2 outline-none" />
        </label>
        {error && <div className="text-sm text-destructive">{error}</div>}
        {info && <div className="text-sm text-muted-foreground">{info}</div>}
        <button type="submit" disabled={busy}
          className="eyebrow border border-foreground px-6 py-3 hover:bg-foreground hover:text-background transition-colors disabled:opacity-50">
          {busy ? t("common.ellipsis") : mode === "signin" ? t("auth.signin") : t("auth.register")}
        </button>
        <button type="button" onClick={() => { setMode(mode === "signin" ? "signup" : "signin"); setError(null); setInfo(null); }}
          className="block text-xs italic text-muted-foreground underline">
          {mode === "signin" ? t("auth.createAccount") : t("auth.alreadyRegistered")}
        </button>
        {mode === "signin" && (
          <Link to="/forgot-password" className="block text-xs italic text-muted-foreground underline">
            {t("auth.forgotPassword")}
          </Link>
        )}
      </form>
      <Link to="/" className="eyebrow inline-block mt-12 border-b border-foreground pb-1">{t("common.backToMagazine")}</Link>
    </div>
  );
}